import { useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from '../api/fetch-with-auth';
import { toast } from 'react-toastify';
import { API_URL } from 'constants/index';
import {
  CustomFieldDefinition,
  CustomFieldValue,
  CustomFieldScope,
  CreateCustomFieldDTO,
  UpdateCustomFieldDTO,
  SaveCustomFieldValueDTO,
  AIExtractionConfig,
} from '../types/custom-field';

interface UseCustomFieldsReturn {
  definitions: CustomFieldDefinition[];
  values: CustomFieldValue[];
  isLoading: boolean;
  error: string | null;
  fetchDefinitions: (scope: CustomFieldScope) => Promise<void>;
  fetchValues: (scope: CustomFieldScope, entityId: string, buildingId?: string) => Promise<void>;
  saveValue: (dto: SaveCustomFieldValueDTO) => Promise<void>;
  bulkSaveValues: (values: SaveCustomFieldValueDTO[]) => Promise<void>;
  createDefinition: (dto: CreateCustomFieldDTO) => Promise<void>;
  updateDefinition: (id: number, dto: UpdateCustomFieldDTO) => Promise<void>;
  deleteDefinition: (id: number) => Promise<void>;
  updateAIConfig: (id: number, config: AIExtractionConfig) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useCustomFields = (): UseCustomFieldsReturn => {
  const { session } = useSession();
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [values, setValues] = useState<CustomFieldValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentScope, setCurrentScope] = useState<CustomFieldScope | null>(null);
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null);
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(null);

  const fetchDefinitions = useCallback(
    async (scope: CustomFieldScope) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);
      setCurrentScope(scope);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/definitions?scope=${scope}`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij ophalen van veld definities');
        }

        const data: CustomFieldDefinition[] = await response.json();
        setDefinitions(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const fetchValues = useCallback(
    async (scope: CustomFieldScope, entityId: string, buildingId?: string) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      if (!entityId) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setCurrentScope(scope);
      setCurrentEntityId(entityId);
      setCurrentBuildingId(buildingId || null);

      try {
        const entityPath = scope === CustomFieldScope.BUILDING ? `building/${entityId}` : `unit/${entityId}`;
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/values/${entityPath}`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij ophalen van veldwaarden');
        }

        const data: CustomFieldValue[] = await response.json();
        setValues(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const saveValue = useCallback(
    async (dto: SaveCustomFieldValueDTO) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/values`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij opslaan van veldwaarde');
        }

        const savedValue: CustomFieldValue = await response.json();

        // Update local state
        setValues((prev) => {
          const existingIndex = prev.findIndex(
            (v) => v.fieldDefinitionId === savedValue.fieldDefinitionId
          );
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = savedValue;
            return updated;
          } else {
            return [...prev, savedValue];
          }
        });

        toast.success('Veldwaarde opgeslagen');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const bulkSaveValues = useCallback(
    async (valueDtos: SaveCustomFieldValueDTO[]) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/values/bulk`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ values: valueDtos }),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij bulk opslaan van veldwaarden');
        }

        const savedValues: CustomFieldValue[] = await response.json();

        // Update local state
        setValues((prev) => {
          const updated = [...prev];
          savedValues.forEach((savedValue) => {
            const existingIndex = updated.findIndex(
              (v) => v.fieldDefinitionId === savedValue.fieldDefinitionId
            );
            if (existingIndex >= 0) {
              updated[existingIndex] = savedValue;
            } else {
              updated.push(savedValue);
            }
          });
          return updated;
        });

        toast.success('Veldwaarden opgeslagen');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const createDefinition = useCallback(
    async (dto: CreateCustomFieldDTO) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/definitions`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij aanmaken van veld definitie');
        }

        const newDefinition: CustomFieldDefinition = await response.json();
        setDefinitions((prev) => [...prev, newDefinition]);
        toast.success('Veld definitie aangemaakt');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const updateDefinition = useCallback(
    async (id: number, dto: UpdateCustomFieldDTO) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/definitions/${id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij bijwerken van veld definitie');
        }

        const updatedDefinition: CustomFieldDefinition = await response.json();
        setDefinitions((prev) =>
          prev.map((def) => (def.id === id ? updatedDefinition : def))
        );
        toast.success('Veld definitie bijgewerkt');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const deleteDefinition = useCallback(
    async (id: number) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/definitions/${id}`,
          { method: 'DELETE' },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij verwijderen van veld definitie');
        }

        setDefinitions((prev) => prev.filter((def) => def.id !== id));
        toast.success('Veld definitie verwijderd');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const updateAIConfig = useCallback(
    async (id: number, config: AIExtractionConfig) => {
      if (!session) {
        throw new Error('Geen actieve sessie');
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/custom-fields/definitions/${id}/ai-config`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(config),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Fout bij bijwerken van AI configuratie');
        }

        const updatedDefinition: CustomFieldDefinition = await response.json();
        setDefinitions((prev) =>
          prev.map((def) => (def.id === id ? updatedDefinition : def))
        );
        toast.success('AI configuratie bijgewerkt');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [session]
  );

  const refresh = useCallback(async () => {
    if (currentScope && currentEntityId) {
      await Promise.all([
        fetchDefinitions(currentScope),
        fetchValues(currentScope, currentEntityId, currentBuildingId || undefined),
      ]);
    } else if (currentScope) {
      await fetchDefinitions(currentScope);
    }
  }, [currentScope, currentEntityId, currentBuildingId, fetchDefinitions, fetchValues]);

  return {
    definitions,
    values,
    isLoading,
    error,
    fetchDefinitions,
    fetchValues,
    saveValue,
    bulkSaveValues,
    createDefinition,
    updateDefinition,
    deleteDefinition,
    updateAIConfig,
    refresh,
  };
};
